import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import Icon, { IconSizes } from '@Components/Icon';
import LoadingSpinner from '@Components/LoadingSpinner';
import { useAppKindContext } from '@Components/root';
import Typography from '@Components/Typography';
import { CategoryFragment } from '@Graphql/graphqlTypes.generated';
import { useTranslateObject } from '@Hooks/useTranslateObject';
import { buildCategoryPageOnCategoriesUrl } from '@Utils/router';
import { transformToTree } from '@Utils/transformations';

import styles from './CategoryTree.module.scss';

type CategoryTreeProps = {
  isLoading: boolean;
  categories: CategoryFragment[];
  selectedCategory: CategoryFragment | undefined;
};

const CategoryTree = ({ isLoading, categories, selectedCategory }: CategoryTreeProps) => {
  const { appKind } = useAppKindContext();
  const selectedId = selectedCategory?.parent?.id || selectedCategory?.id;
  const [expandedCategory, setExpandedCategory] = useState(selectedId);

  React.useEffect(() => {
    setExpandedCategory(selectedId);
  }, [selectedId]);

  const selectTranslation = useTranslateObject();

  const categoriesTree = useMemo(
    () =>
      transformToTree(
        categories.filter(cat => cat.kind === appKind),
        item => item.orderNum,
      ),
    [categories, appKind],
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.container}>
      {categoriesTree.map(parentCategory => {
        const isExpanded = expandedCategory === parentCategory.element.id;
        return (
          <React.Fragment key={parentCategory.element.id}>
            <div className={styles.parentLine}>
              <Link to={buildCategoryPageOnCategoriesUrl(parentCategory.element.slug)}>
                <Typography
                  msg={selectTranslation(parentCategory.element.name)}
                  tag="div"
                  bold
                  color={selectedCategory?.id === parentCategory.element.id ? 'primary' : 'black'}
                />
              </Link>
              {parentCategory.children.length > 0 && (
                <Icon
                  size={IconSizes.s}
                  icon="arrow-down"
                  className={!isExpanded ? styles.rotateArrow : undefined}
                  onClick={() => setExpandedCategory(isExpanded ? undefined : parentCategory.element.id)}
                />
              )}
            </div>
            {isExpanded && (
              <div className={styles.subCategories}>
                {parentCategory.children.map(child => (
                  <Link key={child.element.id} to={buildCategoryPageOnCategoriesUrl(child.element.slug)}>
                    <Typography
                      msg={selectTranslation(child.element.name)}
                      tag="div"
                      color={selectedCategory?.id === child.element.id ? 'primary' : 'black'}
                    />
                  </Link>
                ))}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default CategoryTree;
